import { useAuth0 } from "@auth0/auth0-react";
import axios, { AxiosInstance, AxiosResponse } from "axios";
import React, { memo, useCallback, useEffect, useRef, useState } from "react";
import { Document, Page, pdfjs } from "react-pdf";
import { uploadFilesAsync } from "src/api/file/file-api";
import { useApiOperation } from "src/api/hooks";
import { ApiResponse, Modal } from "src/api/public-types";
import onErrorImage from "src/assets/images/onerror_preview.svg";
import { BaseButton, BaseModal } from "src/components";
import { usePartnerAuthority } from "src/hooks/usePartnerAuthority";
import { BaseAbstractModal } from "./BaseAbstractModal";

// ============================================
// Types
// ============================================

export interface BaseImageFile {
  id?: number | string;
  fileStorageType?: string;
  key?: string;
  url?: string;
  filename?: string;
  contentType?: string;
  fileSize?: number;
  type?: string;
  orderNums?: number;
  blobUrl?: string;
  serviceTag?: string;
  cmdType?: string;
  isDeleted?: boolean;
  isRemoved?: boolean;
  name?: string;
  serviceId?: string;
  mediaServiceType?: string;
  category1?: string;
  category2?: string;
  description?: string;
  isPrimary?: boolean;
}

export interface DragDropState {
  draggedFrom: number | null;
  draggedTo: number | null;
  isDragging: boolean;
  originalOrder: BaseImageFile[];
  updatedOrder: BaseImageFile[];
}

interface ImagesUpdateProps {
  isOnlyView?: boolean;
  urlKey?: string;
  images?: BaseImageFile[];
  setImages?: React.Dispatch<React.SetStateAction<BaseImageFile[]>>;
  className?: string;
  categoryName?: string;
  fileMaxLength?: number;
  accept?: string;
  type: "LOGO" | "PROOF";
  serviceTag?: string;
  maxWidth?: number;
  customDescription?: React.ReactNode;
  customUploadApi?: (params: { file: File }) => Promise<AxiosResponse<ApiResponse<{ media: BaseImageFile }>>>;
  maxSize?: number;
  maxSizeUnit?: "KB" | "MB";
  customValidationMessage?: string;
}

// ============================================
// Constants
// ============================================

const S3_URL = process.env.REACT_APP_S3_BASEURL || "";
const DEFAULT_MAX_FILE_SIZE = 10;
const DEFAULT_MAX_FILE_SIZE_UNIT: "KB" | "MB" = "MB";
const IMAGE_EXTENSIONS = ["jpg", "jpeg", "gif", "png", "svg", "bmp"];

// ============================================
// Utility Functions
// ============================================

const getFileExtension = (filename: string): string => {
  return filename.slice(filename.lastIndexOf(".") + 1).toLowerCase();
};

const splitFilename = (filename: string): { title: string; extension: string } => {
  const dotIndex = filename.lastIndexOf(".");
  return {
    title: filename.slice(0, dotIndex),
    extension: filename.slice(dotIndex + 1),
  };
};

const isImageFile = (filename: string): boolean => {
  const extension = getFileExtension(filename);
  return IMAGE_EXTENSIONS.includes(extension);
};

const readFileAsDataURL = (file: Blob): Promise<string> => {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
};

const buildImageUrl = (file: BaseImageFile, token: string): string => {
  if (file.blobUrl) {
    return file.blobUrl;
  }
  if (file.url) {
    return file.type === "LOGO" ? file.url : `${file.url}?access_token=${token}`;
  }
  if (file.key?.includes("http")) {
    return file.key;
  }
  if (file.key) {
    return `${S3_URL}${file.key}`;
  }
  return "";
};

// ============================================
// API Functions
// ============================================

export async function getFileDownload(axios: AxiosInstance, param: { url: string }): Promise<AxiosResponse<ApiResponse<Blob>>> {
  return axios.get<ApiResponse<Blob>>(param.url, { responseType: "blob" });
}

// ============================================
// PDF Worker Setup
// ============================================

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

// ============================================
// Component
// ============================================

const BaseImageUpload = memo(
  ({
    setImages,
    images,
    serviceTag,
    type,
    fileMaxLength = 10,
    isOnlyView,
    accept,
    maxWidth = 1070,
    customUploadApi,
    customDescription,
    maxSize = DEFAULT_MAX_FILE_SIZE,
    maxSizeUnit = DEFAULT_MAX_FILE_SIZE_UNIT,
    customValidationMessage,
  }: ImagesUpdateProps) => {
    // State
    const [previewImages, setPreviewImages] = useState<BaseImageFile[]>([...(images || [])]);
    const [token, setToken] = useState("");
    const [modalOpen, setModalOpen] = useState<Modal>({ isOpen: false });
    const [isFullScreenOpen, setIsFullScreenOpen] = useState(false);
    const [fullScreenIndex, setFullScreenIndex] = useState(0);
    const [pdfPageNumber, setPdfPageNumber] = useState(1);
    const [pdfNumPages, setPdfNumPages] = useState(0);

    // Refs
    const fileInputRef = useRef<HTMLInputElement>(null);

    // Hooks
    const { isAuthority } = usePartnerAuthority();
    const { executeAsync: postImageFile } = useApiOperation(uploadFilesAsync);
    const { getIdTokenClaims, getAccessTokenSilently } = useAuth0();

    // Derived values
    const maxSizeBytes = maxSizeUnit === "KB" ? maxSize * 1024 : maxSize * 1024 * 1024;
    const validationMessage =
      customValidationMessage || `한 파일당 업로드 용량은 ${maxSize}${maxSizeUnit}, 최대 ${fileMaxLength}개까지만 가능합니다.`;
    const isUploadDisabled = fileMaxLength !== undefined && previewImages.length >= fileMaxLength;

    // ============================================
    // Token Management
    // ============================================

    const fetchToken = useCallback(async () => {
      await getAccessTokenSilently();
      const claims = await getIdTokenClaims();
      if (claims?.__raw) {
        setToken(claims.__raw);
      }
    }, [getAccessTokenSilently, getIdTokenClaims]);

    useEffect(() => {
      if (!token) {
        fetchToken();
      }
    }, [fetchToken, token]);

    // ============================================
    // Sync with parent images
    // ============================================

    useEffect(() => {
      if (previewImages.length !== images?.length) {
        setPreviewImages(images || []);
      }
    }, [images, previewImages.length]);

    // ============================================
    // Keyboard Event Handler
    // ============================================

    useEffect(() => {
      const handleEscapeKey = (e: KeyboardEvent) => {
        if (e.key === "Escape") {
          e.preventDefault();
          setIsFullScreenOpen(false);
        }
      };

      window.addEventListener("keyup", handleEscapeKey);
      return () => window.removeEventListener("keyup", handleEscapeKey);
    }, []);

    // ============================================
    // File Validation
    // ============================================

    const validateFiles = useCallback(
      (files: FileList): boolean => {
        // Check file sizes
        for (let i = 0; i < files.length; i++) {
          if (files[i].size > maxSizeBytes) {
            setModalOpen({ isOpen: true, message: validationMessage });
            return false;
          }
        }

        // Check total count
        const totalCount = files.length + previewImages.length;
        if (fileMaxLength && totalCount > fileMaxLength) {
          setModalOpen({ isOpen: true, message: validationMessage });
          return false;
        }

        return true;
      },
      [fileMaxLength, maxSizeBytes, previewImages.length, validationMessage],
    );

    // ============================================
    // File Upload Handler
    // ============================================

    const handleFileUpload = useCallback(
      async (event: React.ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;
        if (!files || !validateFiles(files)) return;

        const uploadedFiles: BaseImageFile[] = [];

        for (let i = 0; i < files.length; i++) {
          const file = files[i];
          const formData = new FormData();
          formData.append("file", file);

          try {
            const response = customUploadApi
              ? await customUploadApi({ file })
              : await postImageFile({ file: formData, type: type === "LOGO" ? "public" : "private" });

            const mediaData: BaseImageFile = {
              ...response.data.data.media,
              type,
              serviceTag,
              isDeleted: false,
            };

            const blobUrl = await readFileAsDataURL(file);
            uploadedFiles.push({ ...mediaData, blobUrl });
          } catch (error) {
            console.error("Error uploading file:", error);
          }
        }

        const newImages = [...uploadedFiles, ...(images || [])];
        setImages?.(newImages);
        setPreviewImages(newImages);
        event.target.value = "";
      },
      [customUploadApi, images, postImageFile, serviceTag, setImages, type, validateFiles],
    );

    // ============================================
    // Image Actions
    // ============================================

    const handleImageRemove = useCallback(
      (index: number, e: React.MouseEvent) => {
        e.stopPropagation();
        const updatedImages = previewImages.filter((_, i) => i !== index);
        setPreviewImages(updatedImages);
        setImages?.(updatedImages);
      },
      [previewImages, setImages],
    );

    const handleImageDownload = useCallback(
      async (filename: string, url: string) => {
        try {
          const response = await axios.get(`${url}?access_token=${token}`, {
            responseType: "blob",
          });
          const blob = new Blob([response.data]);
          const blobURL = URL.createObjectURL(blob);
          const link = document.createElement("a");
          link.href = blobURL;
          link.download = filename;
          link.click();
          URL.revokeObjectURL(blobURL);
        } catch (error) {
          console.error("Download failed:", error);
        }
      },
      [token],
    );

    // ============================================
    // Full Screen Navigation
    // ============================================

    const openFullScreen = useCallback((index: number) => {
      setFullScreenIndex(index);
      setIsFullScreenOpen(true);
    }, []);

    const navigateFullScreen = useCallback(
      (direction: "left" | "right") => {
        setFullScreenIndex((prev) => {
          if (direction === "left" && prev > 0) return prev - 1;
          if (direction === "right" && prev < previewImages.length - 1) return prev + 1;
          return prev;
        });
      },
      [previewImages.length],
    );

    // ============================================
    // PDF Handlers
    // ============================================

    const handlePdfLoadSuccess = useCallback(({ numPages }: { numPages: number }) => {
      setPdfNumPages(numPages);
    }, []);

    const navigatePdf = useCallback(
      (direction: "left" | "right") => {
        setPdfPageNumber((prev) => {
          if (direction === "left" && prev > 1) return prev - 1;
          if (direction === "right" && prev < pdfNumPages) return prev + 1;
          return prev;
        });
      },
      [pdfNumPages],
    );

    // ============================================
    // Render Helpers
    // ============================================

    const renderFilePreview = useCallback(
      (file: BaseImageFile) => {
        const fileUrl = file.blobUrl || `${file.url || S3_URL + file.key}?access_token=${token}`;
        const filename = file.filename || "";
        const extension = getFileExtension(filename);

        if (isImageFile(filename)) {
          return (
            <>
              <img
                src={buildImageUrl(file, token)}
                alt={filename || "이미지를 불러오는데 실패했습니다."}
                onError={({ currentTarget }) => {
                  currentTarget.onerror = null;
                  currentTarget.src = onErrorImage;
                }}
              />
              <p className="file-name">{filename}</p>
            </>
          );
        }

        if (extension === "pdf") {
          return (
            <div className="file-viewer-pdf">
              {token && (
                <Document className="file-viewer-pdf__document" file={fileUrl} onLoadSuccess={handlePdfLoadSuccess} onLoadError={console.error}>
                  <Page pageNumber={pdfPageNumber} renderAnnotationLayer={false} loading="loading..." onLoadError={console.error} />
                </Document>
              )}
              <div className="button-wrap">
                <button className="left" disabled={pdfPageNumber === 1} onClick={() => navigatePdf("left")} />
                <button className="right" disabled={pdfPageNumber === pdfNumPages} onClick={() => navigatePdf("right")} />
              </div>
            </div>
          );
        }

        return (
          <div className="no-preview-available">
            <p>미리보기를 제공하지 않는 파일 형식입니다.</p>
            <p>다운로드 후 확인해 주세요.</p>
            <p className="file-name">{filename}</p>
          </div>
        );
      },
      [handlePdfLoadSuccess, navigatePdf, pdfNumPages, pdfPageNumber, token],
    );

    const renderImageItem = useCallback(
      (image: BaseImageFile, index: number) => {
        const { title, extension } = splitFilename(image.filename || "");

        return (
          <div
            className={`base-image__wrap ${isOnlyView ? "detail" : "form"}`}
            key={index}
            draggable
            data-position={index}
            onClick={() => openFullScreen(index)}
          >
            <img
              src={buildImageUrl(image, token)}
              alt="preview-images"
              onError={({ currentTarget }) => {
                currentTarget.onerror = null;
                currentTarget.src = onErrorImage;
              }}
            />
            {!isOnlyView && <button type="button" onClick={(e) => handleImageRemove(index, e)} />}
            <div className="title-area">
              <div className="not-hover">
                <p>{title}</p>
                <span>.{extension}</span>
              </div>
              <div className="active-hover">
                <p>{title}</p>
                <span>.{extension}</span>
                {isOnlyView && (
                  <button
                    type="button"
                    className="detail-download"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleImageDownload(image.filename || "", image.url || "");
                    }}
                  />
                )}
              </div>
            </div>
          </div>
        );
      },
      [handleImageDownload, handleImageRemove, isOnlyView, openFullScreen, token],
    );

    // ============================================
    // Render
    // ============================================

    return (
      <div className="base-image-wrap" style={{ width: maxWidth }}>
        {/* Upload Controls */}
        {!isOnlyView &&
          (isAuthority("w", "BASIC") ? (
            <>
              <input ref={fileInputRef} type="file" style={{ display: "none" }} accept={accept || "*"} onChange={handleFileUpload} multiple />
              <BaseButton title="파일찾기" className="color-white" disabled={isUploadDisabled} onClick={() => fileInputRef.current?.click()} />
              {previewImages.length === 0 &&
                (customDescription || (
                  <ul className="base-list mt10">
                    <li>문서를 카메라로 찍거나 스캔하여 업로드해주세요.</li>
                    <li>
                      업로드 용량은 개당 {maxSize}
                      {maxSizeUnit}까지 가능합니다.
                    </li>
                  </ul>
                ))}
            </>
          ) : (
            previewImages.length === 0 && <p className="font14">-</p>
          ))}

        {/* Image Preview Grid */}
        <div className="base-image" style={{ overflowX: "auto" }}>
          {previewImages.map(renderImageItem)}

          {/* Full Screen Modal */}
          {isFullScreenOpen && previewImages[fullScreenIndex] && (
            <BaseAbstractModal isNotUseWhiteBoard isOpen opacity={0.75}>
              <div className="base-image__full-screen">
                {renderFilePreview(previewImages[fullScreenIndex])}
                <div className="img-button-wrap">
                  <div className="flicker">
                    <button className="left" onClick={() => navigateFullScreen("left")} />
                    <p>
                      {fullScreenIndex + 1} / {previewImages.length}
                    </p>
                    <button className="right" onClick={() => navigateFullScreen("right")} />
                  </div>
                  <a
                    target="_self"
                    className="download"
                    href=" "
                    rel="noreferrer"
                    onClick={(e) => {
                      e.preventDefault();
                      const currentImage = previewImages[fullScreenIndex];
                      handleImageDownload(currentImage.filename || "", currentImage.url || "");
                    }}
                  >
                    {""}
                  </a>
                  <button className="close" onClick={() => setIsFullScreenOpen(false)}>
                    닫기
                  </button>
                </div>
              </div>
            </BaseAbstractModal>
          )}

          {/* Error Modal */}
          {modalOpen.isOpen && (
            <BaseModal isOpen title="첨부파일 업로드가 실패했습니다." btnRightTitle="확인" onClick={() => setModalOpen({ isOpen: false })}>
              <p>{modalOpen.message}</p>
            </BaseModal>
          )}
        </div>
      </div>
    );
  },
);

export default BaseImageUpload;
